import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Button,
  Tabs,
  Tab,
  Badge,
  Divider,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  ListItemAvatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Notifications,
  NotificationsActive,
  NotificationsNone,
  NotificationsOff,
  CheckCircle,
  Warning,
  Info,
  Error as ErrorIcon,
  Delete,
  MarkEmailRead,
  MarkEmailUnread,
  FilterList,
  MoreVert,
  Assignment,
  Description,
  AttachMoney,
  Security,
  Event,
  Person,
  Schedule,
} from '@mui/icons-material';
import { useNotifications } from '../../contexts/NotificationContext';
import toast from 'react-hot-toast';
import { getAuthToken } from '../../lib/authToken';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`notifications-tabpanel-${index}`}
      aria-labelledby={`notifications-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  category: 'case' | 'document' | 'billing' | 'security' | 'system' | 'general';
  read: boolean;
  createdAt: string;
  priority: 'low' | 'medium' | 'high';
  actionUrl?: string;
  sender?: string;
}

async function apiNotify(path: string, options: RequestInit = {}) {
  const token = getAuthToken();
  const response = await fetch(`/api/notifications${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Notification request failed');
  }
  return response.json();
}

const NotificationsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const { refreshUnreadCount, markAsRead, markAsUnread } = useNotifications();

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const json = await apiNotify('/');
      setNotifications(Array.isArray(json.data) ? json.data : []);
      await refreshUnreadCount();
    } catch (err: any) {
      setLoadError(err.message || 'Unable to load notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [refreshUnreadCount]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      try {
        await apiNotify(`/${notification.id}/read`, { method: 'PUT' });
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
        );
        markAsRead();
      } catch (err: any) {
        toast.error(err.message || 'Failed to mark as read');
      }
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await apiNotify(`/${notificationId}/read`, { method: 'PUT' });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      markAsRead();
    } catch (err: any) {
      toast.error(err.message || 'Failed to mark as read');
    }
  };

  const handleMarkAsUnread = async (notificationId: string) => {
    try {
      await apiNotify(`/${notificationId}/unread`, { method: 'PUT' });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: false } : n))
      );
      markAsUnread();
    } catch (err: any) {
      toast.error(err.message || 'Failed to mark as unread');
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    const notification = notifications.find((n) => n.id === notificationId);
    try {
      await apiNotify(`/${notificationId}`, { method: 'DELETE' });
      if (notification && !notification.read) {
        markAsRead();
      }
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete notification');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, notification: Notification) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedNotification(notification);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNotification(null);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await apiNotify('/mark-all-read', { method: 'PUT' });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      await refreshUnreadCount();
    } catch (err: any) {
      toast.error(err.message || 'Failed to mark all as read');
    }
  };

  const handleClearAll = async () => {
    try {
      await apiNotify('/clear-all', { method: 'DELETE' });
      setNotifications([]);
      await refreshUnreadCount();
    } catch (err: any) {
      toast.error(err.message || 'Failed to clear notifications');
    }
  };

  const getNotificationIcon = (type: string, category: string) => {
    switch (category) {
      case 'case':
        return <Assignment color={type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'primary'} />;
      case 'document':
        return <Description color={type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'primary'} />;
      case 'billing':
        return <AttachMoney color={type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'primary'} />;
      case 'security':
        return <Security color={type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'primary'} />;
      case 'system':
        return <Info color={type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'primary'} />;
      default:
        return <Notifications color={type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'primary'} />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const allNotifications = notifications;
  const unreadNotifications = notifications.filter(n => !n.read);
  const readNotifications = notifications.filter(n => n.read);

  const renderNotificationItem = (notification: Notification) => (
    <ListItem
      key={notification.id}
      sx={{
        backgroundColor: notification.read ? 'transparent' : 'action.hover',
        borderLeft: `4px solid ${theme.palette[getTypeColor(notification.type)].main}`,
        mb: 1,
        borderRadius: 1,
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
      button
      onClick={() => handleNotificationClick(notification)}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: `${getTypeColor(notification.type)}.light` }}>
          {getNotificationIcon(notification.type, notification.category)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" component="span" sx={{ fontWeight: notification.read ? 'normal' : 'bold' }}>
              {notification.title}
            </Typography>
            <Chip 
              label={notification.priority} 
              size="small" 
              color={getPriorityColor(notification.priority) as any}
            />
          </Box>
        }
        secondary={
          <Box component="span">
            <Typography variant="body2" component="span" color="text.secondary">
              {notification.message}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" component="span">
              {formatTimeAgo(notification.createdAt)} • {notification.sender}
            </Typography>
          </Box>
        }
      />
      <ListItemSecondaryAction>
        <IconButton
          edge="end"
          onClick={(e) => handleMenuOpen(e, notification)}
        >
          <MoreVert />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );

  const renderAllNotifications = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="span">
          All Notifications ({allNotifications.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<MarkEmailRead />}
            onClick={handleMarkAllAsRead}
            disabled={unreadCount === 0}
          >
            Mark All Read
          </Button>
          <Button
            size="small"
            startIcon={<Delete />}
            onClick={handleClearAll}
            disabled={allNotifications.length === 0}
            color="error"
          >
            Clear All
          </Button>
        </Box>
      </Box>
      <List>
        {allNotifications.map(renderNotificationItem)}
      </List>
    </Box>
  );

  const renderUnreadNotifications = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="span">
          Unread Notifications ({unreadNotifications.length})
        </Typography>
        <Button
          size="small"
          startIcon={<MarkEmailRead />}
          onClick={handleMarkAllAsRead}
          disabled={unreadCount === 0}
        >
          Mark All Read
        </Button>
      </Box>
      <List>
        {unreadNotifications.map(renderNotificationItem)}
      </List>
    </Box>
  );

  const renderReadNotifications = () => (
    <Box>
      <Typography variant="h6" component="span" sx={{ mb: 2 }}>
        Read Notifications ({readNotifications.length})
      </Typography>
      <List>
        {readNotifications.map(renderNotificationItem)}
      </List>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Badge badgeContent={unreadCount} color="error">
          <Notifications sx={{ fontSize: 32, color: 'primary.main' }} />
        </Badge>
        <Typography variant="h4" component="h1">
          Notifications
        </Typography>
      </Box>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={loadNotifications}>Retry</Button>
        }>
          {loadError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="notifications tabs">
              <Tab label="All" icon={<Notifications />} iconPosition="start" />
              <Tab label="Unread" icon={<NotificationsActive />} iconPosition="start" />
              <Tab label="Read" icon={<NotificationsNone />} iconPosition="start" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            {allNotifications.length === 0 ? (
              <Typography color="text.secondary">No notifications yet.</Typography>
            ) : (
              renderAllNotifications()
            )}
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {unreadNotifications.length === 0 ? (
              <Typography color="text.secondary">No unread notifications.</Typography>
            ) : (
              renderUnreadNotifications()
            )}
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            {readNotifications.length === 0 ? (
              <Typography color="text.secondary">No read notifications.</Typography>
            ) : (
              renderReadNotifications()
            )}
          </TabPanel>
        </>
      )}

      {/* Notification Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedNotification && (
          <>
            {selectedNotification.read ? (
              <MenuItem onClick={() => {
                handleMarkAsUnread(selectedNotification.id);
                handleMenuClose();
              }}>
                <ListItemIcon>
                  <MarkEmailUnread fontSize="small" />
                </ListItemIcon>
                Mark as Unread
              </MenuItem>
            ) : (
              <MenuItem onClick={() => {
                handleMarkAsRead(selectedNotification.id);
                handleMenuClose();
              }}>
                <ListItemIcon>
                  <MarkEmailRead fontSize="small" />
                </ListItemIcon>
                Mark as Read
              </MenuItem>
            )}
            <MenuItem onClick={() => {
              handleDeleteNotification(selectedNotification.id);
              handleMenuClose();
            }}>
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              Delete
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default NotificationsPage; 